import React, { useEffect, useState } from "react";
import Loader from "../../Shared/Loader";

import ConfirmationModal from "./ConfirmationModal";
import "../../../css/EditQavachStaffModal.css";
import axios from "axios";
import api from '../../../api/axiosConfig';
import { showToast } from '../../Shared/Toaster';


interface QavachUser {
  _id: string;
  email: string;
  displayName: string;
  roleType: string;
  status: string;
  role: string;
  createdAt: string;
  disabledAt?: string | null;
}

interface Role {
  _id: string;
  name: string;
}

interface EditQavachStaffModalProps {
  user: QavachUser;
  onClose: () => void;
  onUpdate: (updatedUser: QavachUser) => void;
}

const EditQavachStaffModal: React.FC<EditQavachStaffModalProps> = ({
  user,
  onClose,
  onUpdate,
}) => {
  // const { instance, accounts } = useMsal();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formData, setFormData] = useState({
    roleId: "",
    status: user.status || "ACTIVE",
    notes: "",
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {

      const token = sessionStorage.getItem("accessToken");

      const res = await api.auth_api.get("/roles", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const rolesData: Role[] = res.data;
      setRoles(rolesData);

      // Preselect role if user already has one
      if (user.role) {
        const matchedRole = rolesData.find((role) => role.name === user.role);
        if (matchedRole) {
          setFormData((prev) => ({ ...prev, roleId: matchedRole._id }));
        }
      }
    } catch (err) {
      console.error("❌ Error fetching roles:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.roleId) {
      showToast("Please select a role", 'error');
      return;
    }

    if (formData.status === "DISABLED") {
      setShowConfirm(true);
    } else {
      updateUserRole();
    }
  };

const updateUserRole = async () => {
  setUpdating(true);
  try {

    const token = sessionStorage.getItem("accessToken");

    const res = await api.auth_api.post(`/users/${user._id}/roles`,
      {
        roleId: formData.roleId,
        status: formData.status,
        notes: formData.notes,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Make sure the API returns the updated user role data
    const updatedData = res.data;
    
    showToast("✅ User role updated successfully!", 'success');

    // Update with the data returned from the API
    onUpdate({
      ...user,
      role: roles.find((r) => r._id === formData.roleId)?.name || user.role,
      status: formData.status,
      createdAt: updatedData.assignedAt || user.createdAt,
      disabledAt: formData.status === "DISABLED" 
        ? new Date().toISOString() 
        : null
    });

    onClose();
  } catch (err) {
    console.error("❌ Error updating user role:", err);
    showToast("Failed to update user role.", 'error');
  } finally {
    setUpdating(false);
    setShowConfirm(false);
  }
};

  const handleCancelDisable = () => {
    setShowConfirm(false);
  };

  return (
    <div className="edit-qavach-staff-modal-overlay" onClick={onClose}>
      <div className="edit-qavach-staff-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="edit-qavach-staff-modal-header">
          <h2>Edit Qavach Staff Role</h2>
          <button className="edit-qavach-staff-close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="edit-qavach-staff-modal-body">
            <div className="edit-qavach-staff-field-group">
              <label>User ID</label>
              <p className="edit-qavach-staff-field-value">{user.email}</p>
            </div>

            <div className="edit-qavach-staff-field-group">
              <label>User Name</label>
              <p className="edit-qavach-staff-field-value">{user.displayName}</p>
            </div>

            <div className="edit-qavach-staff-field-group">
              <label>Role</label>
              {loading ? (
                <Loader size="small" text="Loading roles..." />
              ) : (
                <select
                  className="edit-qavach-staff-form-input"
                  value={formData.roleId}
                  onChange={(e) => handleInputChange("roleId", e.target.value)}
                  required
                >
                  <option value="">Select Role</option>
                  {roles.map((role) => (
                    <option key={role._id} value={role._id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="edit-qavach-staff-field-group">
              <label>Status</label>
              <div className="edit-qavach-staff-status-radio-group">
                <label>
                  <input
                    type="radio"
                    name="status"
                    value="ACTIVE"
                    checked={formData.status === "ACTIVE"}
                    onChange={(e) => handleInputChange("status", e.target.value)}
                  />
                  Active
                </label>
                <label>
                  <input
                    type="radio"
                    name="status"
                    value="DISABLED"
                    checked={formData.status === "DISABLED"}
                    onChange={(e) => handleInputChange("status", e.target.value)}
                  />
                  Disabled
                </label>
              </div>
            </div>

            <div className="edit-qavach-staff-field-group">
              <label>Additional Information</label>
              <textarea
                className="edit-qavach-staff-additional-info"
                placeholder="Add any additional notes here..."
                rows={4}
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
              />
            </div>
          </div>

          <div className="edit-qavach-staff-modal-footer">
            <button
              type="button"
              className="edit-qavach-staff-btn btn-cancel"
              onClick={onClose}
              disabled={updating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="edit-qavach-staff-btn-btn-update"
              disabled={updating}
            >
              {updating ? "Updating..." : "Update"}
            </button>
          </div>
        </form>
      </div>

      {showConfirm && (
        <ConfirmationModal
          message="Are you sure you want to disable this user?"
          onConfirm={updateUserRole}
          onCancel={handleCancelDisable}
        />
      )}
    </div>
  );
};

export default EditQavachStaffModal;
